import React, { FC, useEffect, useState, useLayoutEffect } from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react'

import Modal from 'components/Modal'
import { toast } from 'App'
import { CustomEditor } from 'components/CustomEditor'
import { IOption, OwnSelect } from './OwnSelect'
import { BroadcastKeyTypeSelect } from './BroadcastKeyTypeSelect'
import ButtonIcon from 'components/ButtonIcon'
import { Tags } from './Tags'
import Button from '../Button'

import { ViewTypeEditor } from 'utils'
import { IStreamOptions } from 'models'
import { useStore } from 'store'
import { convertFromRaw, EditorState } from 'draft-js'
import { extractHashtagsWithIndices } from '@draft-js-plugins/hashtag'
import uniqueId from 'lodash/uniqueId'
import {
  copyToClipboardOnAndroid,
  getPostLengthFromEditorState,
  getPostTextFromEditorState,
  videoMock,
} from 'utils'

import { ReactComponent as CopyIcon } from 'sources/images/copy_link.svg'
import { ReactComponent as LockIcon } from 'sources/images/encryptedIcon.svg'
import { ReactComponent as RefreshIcon } from 'sources/images/refreshOneArrow.svg'
import { ReactComponent as EyeOpenIcon } from 'sources/images/eye.svg'
import { ReactComponent as EyeClosedIcon } from 'sources/images/eyeClosed.svg'

import styles from './styles.module.scss'

interface IProps {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
}

interface IHashtag {
  tag: string
  id: string
  correct: boolean
}

export const StreamSettingsModal: FC<IProps> = observer(
  ({ isOpen, setIsOpen }) => {
    const { mediaQuery, videoStream, loading, videoChat } = useStore()

    const [titleState, setTitleState] = useState(EditorState.createEmpty())
    const [liveNotificationState, setLiveNotificationState] = useState(
      EditorState.createEmpty()
    )
    const [descriptionState, setDescriptionState] = useState(
      EditorState.createEmpty()
    )
    const [tags, setTags] = useState<IHashtag[]>([])

    const [categories, setCategories] = useState<IOption[]>([])
    const [selectedCategories, setSelectedCategories] = useState<IOption[]>([])
    const [isOpenCategorySelect, setIsOpenCategorySelect] =
      useState<boolean>(false)

    const [isFirstStreamOptions, setIsFirstStreamOptions] = useState(false)

    // const [contentClassification, setContentClassification] = useState<IOption[]>(CategoryMock)
    // const [selectedContentClassification, setSelectedContentClassification] = useState<IOption[]>([])
    // const [isOpenContentClassificationSelect, setIsOpenContentClassificationSelect] = useState<boolean>(false)

    // const [broadcastKeyType, setBroadcastKeyType] = useState<IOption[]>(CategoryMock)
    // const [selectedBroadcastKeyType, setSelectedBroadcastKeyType] = useState<IOption[]>([])
    // const [isOpenBroadcastKeyTypeSelect, setIsOpenBroadcastKeyTypeSelect] = useState<boolean>(false)

    const [broadcastKey, setBroadcastKey] = useState<string>('')
    const [isVisibleBroadcastKey, setIsVisibleBroadcastKey] =
      useState<boolean>(false)
    const [isRefreshingBroadcastKey, setIsRefreshingBroadcastKey] =
      useState(false)
    const [broadcastURL, setBroadcastURL] = useState<string>(
      'rtmps://global-live.mux.com:443/app'
    )
    const [backupServerURL, setBackupServerURL] = useState<string>(
      'rtmps://global-live.mux.com:443/app'
    )

    const titleCharacters = getPostTextFromEditorState(titleState)
    const titleCharactersQuantity = titleCharacters.length
    const titleCharactersTrimQuantity = titleCharacters.trim().length

    useLayoutEffect(() => {
      loading.setLoading(true)
    }, [])

    useEffect(() => {
      getStreamOptions()
    }, [])

    const onChangeTags = (value: IHashtag[]) => {
      setTags(value)
    }

    const getStreamOptions = async () => {
      try {
        const response: IStreamOptions | null =
          await videoStream.getStreamOptions()
        if (response) {
          if (!response.Title) {
            setIsFirstStreamOptions(true)
          }
          response.Title &&
            setTitleState(
              EditorState.createWithContent(
                convertFromRaw(JSON.parse(response.Title))
              )
            )
          response.Description &&
            setDescriptionState(
              EditorState.createWithContent(
                convertFromRaw(JSON.parse(response.Description))
              )
            )
          response.Tags &&
            setTags(
              response.Tags.split(',').map(item => ({
                tag: item,
                id: uniqueId(),
                correct: true,
              }))
            )
          response.TwitterNotification &&
            setLiveNotificationState(
              EditorState.createWithContent(
                convertFromRaw(JSON.parse(response.TwitterNotification))
              )
            )
          setBroadcastKey(response.StreamKey)
          const categories: IOption[] = await getAllCategories()
          if (categories) {
            setCategories(categories)
            response.CategoryId &&
              setSelectedCategories(
                categories.filter(item => item.Id === response.CategoryId)
              )
          }
        }
        loading.setLoading(false)
      } catch (e) {
        loading.setLoading(false)
        closeModal()
        toast({
          type: 'warning',
          message: 'An error occurred when create key',
        })
      }
    }

    const saveFirstLiveStreamSettings = async () => {
      try {
        loading.setLoading(true)
        const options = {
          title: titleState,
          description: descriptionState,
          tags: tags
            .filter(item => item.correct)
            .map(item => item.tag)
            .join(','),
          categoryId: selectedCategories.length
            ? selectedCategories[0].Id
            : null,
          twitterNotification: liveNotificationState,
        }
        await videoStream.updateStreamOptions(options)
        setIsFirstStreamOptions(false)
        loading.setLoading(false)
      } catch (e) {
        toast({
          type: 'error',
          message: `An error occurred while updating the options. Please try again.`,
        })
        loading.setLoading(false)
      }
    }

    const updateLiveStreamSettings = async () => {
      try {
        loading.setLoading(true)
        const options = {
          title: titleState,
          description: descriptionState,
          tags: tags
            .filter(item => item.correct)
            .map(item => item.tag)
            .join(','),
          categoryId: selectedCategories.length
            ? selectedCategories[0].Id
            : null,
          twitterNotification: liveNotificationState,
        }
        await videoStream.updateStreamOptions(options)
        loading.setLoading(false)
        toast({
          type: 'success',
          message: 'Stream options successfully updated',
        })
        closeModal()
      } catch (e) {
        toast({
          type: 'error',
          message: `An error occurred while updating the options. Please try again.`,
        })
        loading.setLoading(false)
      }
    }

    const refreshBroadcastKey = async () => {
      try {
        setIsRefreshingBroadcastKey(true)
        const response = await videoStream.regenerateStreamKey()
        setBroadcastKey(response.StreamKey)
        setIsRefreshingBroadcastKey(false)
        toast({
          type: 'success',
          message: 'Broadcast key successfully refreshed',
        })
      } catch (e) {
        setIsRefreshingBroadcastKey(false)
        toast({
          type: 'warning',
          message: 'An error occurred when refresh key',
        })
      }
    }

    const getAllCategories = async () => {
      try {
        return await videoChat.getAllCategories()
      } catch (e) {
        toast({
          type: 'warning',
          message: 'An error occurred when retrieving the categories',
        })
      }
    }

    const onClickSelectCategories = () => {
      setIsOpenCategorySelect(prev => !prev)
    }

    // const onClickSelectContentClassification = () => {
    //   setIsOpenContentClassificationSelect(prev => !prev)
    // }
    //
    // const onClickSelectBroadcastKey = () => {
    //   setIsOpenBroadcastKeyTypeSelect(prev => !prev)
    // }

    const getHashtags = (editorState: EditorState) => {
      return [
        ...new Set(
          extractHashtagsWithIndices(
            getPostTextFromEditorState(editorState)
          ).map(item => item.hashtag)
        ),
      ]
    }

    const handleChangeTitle = (editorState: EditorState) => {
      setTitleState(editorState)
    }

    const handleChangeLiveNotification = (editorState: EditorState) => {
      setLiveNotificationState(editorState)
    }

    const handleChangeDescription = (editorState: EditorState) => {
      setDescriptionState(editorState)
    }

    const closeModal = () => {
      setIsOpen(false)
    }

    const copyToClipboard = async (type: 'key' | 'url' | 'serverUrl') => {
      let data = { copyValue: '', typeMessage: '' }
      if (type === 'key')
        data = { copyValue: broadcastKey, typeMessage: 'Broadcast key' }
      if (type === 'url')
        data = { copyValue: broadcastURL, typeMessage: 'Broadcast URL' }
      if (type === 'serverUrl')
        data = { copyValue: backupServerURL, typeMessage: 'Backup server URL' }

      if (window.navigator.clipboard && window.isSecureContext) {
        try {
          await window.navigator.clipboard.writeText(data.copyValue)
          toast({
            type: 'success',
            message: `${data.typeMessage} has been copied`,
          })
        } catch (err) {
          try {
            await copyToClipboardOnAndroid(data.copyValue)
            toast({
              type: 'success',
              message: `${data.typeMessage} has been copied`,
            })
          } catch (err) {
            toast({
              type: 'error',
              message: `Failed to copy`,
            })
          }
        }
      }
    }

    const handleChangeBroadcastKeyVisibility = () => {
      setIsVisibleBroadcastKey(prev => !prev)
    }

    const broadcastKeyVisible = isVisibleBroadcastKey
      ? broadcastKey
      : Array.from({ length: broadcastKey.length }, (_, i) => '●').join('')

    return (
      <Modal
        blockOutsideClose
        size="responsiveMedium"
        contentStyle={{ maxWidth: 'min(768px, 95vw)' }}
        onClose={closeModal}
        open={isOpen}
        closeButton={true}
        children={
          <div className={styles.streamSettingsContainer}>
            <div className={styles.modalTitle}>Edit stream info</div>
            <div className={styles.fieldContainer}>
              <div className={styles.fieldTitle}>
                Stream title<span>*</span>
                <div
                  className={cn({
                    [styles.charactersQuantity]: true,
                    [styles.charactersQuantityError]:
                      titleCharactersQuantity > 200,
                  })}
                >
                  {titleCharactersQuantity}/200
                </div>
              </div>
              <CustomEditor
                viewType={ViewTypeEditor.editorWithoutAvatar}
                editorAutoHeightWithMaxHeight
                withoutToolbar
                placeholder={'Stream title'}
                editorState={titleState}
                setEditorState={handleChangeTitle}
                styleContainer={{
                  margin: 0,
                  padding: mediaQuery.minWidth_2200 ? '20px' : '13px 20px',
                }}
              />
            </div>

            {/*<div className={styles.fieldContainer}>*/}
            {/*  <div className={styles.fieldTitle}>*/}
            {/*    Go live Twitter notification*/}
            {/*    <ButtonIcon*/}
            {/*      style={{*/}
            {/*        minWidth: mediaQuery.minWidth_2200 ? '16px' : '13px',*/}
            {/*        width: mediaQuery.minWidth_2200 ? '16px' : '13px',*/}
            {/*        height: mediaQuery.minWidth_2200 ? '16px' : '13px',*/}
            {/*        margin: '0 0 2px 5px',*/}
            {/*        alignSelf: 'flex-end'*/}
            {/*      }}*/}
            {/*      styleIcon={{*/}
            {/*        width: mediaQuery.minWidth_2200 ? '8px' : '7px',*/}
            {/*        height: mediaQuery.minWidth_2200 ? '8px' : '7px'*/}
            {/*      }}*/}
            {/*      icon={'info'}*/}
            {/*      tooltip="Here we learn JavaScript, starting from scratch and go on to advanced concepts like OOP."*/}
            {/*      tooltipPosition="top"*/}
            {/*      bordered*/}
            {/*    />*/}
            {/*    <div className={cn({*/}
            {/*      [styles.charactersQuantity]: true,*/}
            {/*      [styles.charactersQuantityError]: titleCharactersQuantity > 200*/}
            {/*    })}>*/}
            {/*      {titleCharactersQuantity}/200*/}
            {/*    </div>*/}
            {/*  </div>*/}
            {/*  <CustomEditor viewType={ViewTypeEditor.editorWithoutAvatar}*/}
            {/*                editorAutoHeight*/}
            {/*                withoutToolbar*/}
            {/*                placeholder={'Add notification text'}*/}
            {/*                editorState={liveNotificationState}*/}
            {/*                setEditorState={handleChangeLiveNotification}*/}
            {/*                styleContainer={{ margin: 0 }}/>*/}
            {/*  <div className={styles.fieldRules}>You can only send one notification per stream.</div>*/}
            {/*</div>*/}

            <div className={styles.fieldContainer}>
              <div className={styles.fieldTitle}>Category</div>
              <OwnSelect
                isOpenSelect={isOpenCategorySelect}
                onClickSelect={onClickSelectCategories}
                options={categories}
                setOptions={setCategories}
                selectedOptions={selectedCategories}
                setSelectedOptions={setSelectedCategories}
                selectTitle={'Select category'}
                // addButton
                searchBlock
              />
            </div>

            <div className={styles.fieldContainer}>
              <div className={styles.fieldTitle}>Tags</div>
              <Tags onChange={onChangeTags} tags={tags} />
              <div className={styles.fieldRules}>
                Add up to 10 tags. Each tag can be 25 characters long with no
                spaces or special characters.
              </div>
            </div>

            <div className={styles.fieldContainer}>
              <div className={styles.fieldTitle}>Description</div>
              <CustomEditor
                viewType={ViewTypeEditor.editorWithoutAvatar}
                editorAutoHeight
                withoutToolbar
                placeholder={'Description'}
                editorState={descriptionState}
                setEditorState={handleChangeDescription}
                styleContainer={{
                  margin: 0,
                  padding: mediaQuery.minWidth_2200 ? '20px' : '13px 20px',
                }}
              />
            </div>

            {/*<div className={styles.fieldContainer}>*/}
            {/*  <div className={styles.fieldTitle}>*/}
            {/*    Content Classification*/}
            {/*  </div>*/}
            {/*  <OwnSelect multipleSelect*/}
            {/*             isOpenSelect={isOpenContentClassificationSelect}*/}
            {/*             onClickSelect={onClickSelectContentClassification}*/}
            {/*             options={contentClassification}*/}
            {/*             setOptions={setContentClassification}*/}
            {/*             selectedOptions={selectedContentClassification}*/}
            {/*             setSelectedOptions={setSelectedContentClassification}*/}
            {/*             selectTitle={'Select content labels'}*/}
            {/*             searchBlock*/}
            {/*  />*/}
            {/*  <div className={styles.matureContent}>Mature content</div>*/}
            {/*  <div className={styles.fieldRules}>You are requiered to disclose if your content is not suitable for certain*/}
            {/*    viewers.*/}
            {/*  </div>*/}
            {/*  <div className={styles.matureContentLearnMore}>Learn more</div>*/}
            {/*</div>*/}

            {/*<div className={styles.fieldContainer}>*/}
            {/*  <div className={styles.fieldTitle}>*/}
            {/*    Select the type of broadcast key*/}
            {/*  </div>*/}
            {/*  <BroadcastKeyTypeSelect isOpenSelect={isOpenBroadcastKeyTypeSelect}*/}
            {/*                          onClickSelect={onClickSelectBroadcastKey}/>*/}
            {/*</div>*/}

            {!isFirstStreamOptions ? (
              <>
                <div className={styles.fieldContainer}>
                  <div className={styles.fieldTitle}>Broadcast key</div>
                  <div
                    className={cn({
                      [styles.fieldBox]: true,
                      [styles.fieldBoxDisabled]: isRefreshingBroadcastKey,
                    })}
                  >
                    <div className={styles.fieldBoxInner}>
                      <RefreshIcon
                        onClick={refreshBroadcastKey}
                        className={cn({
                          [styles.refreshIcon]: true,
                          [styles.refreshing]: isRefreshingBroadcastKey,
                        })}
                      />
                      <div className={styles.fieldValue}>
                        {broadcastKeyVisible}
                      </div>
                    </div>
                    <div
                      className={styles.fieldBoxInner}
                      style={{ width: '58px', minWidth: '58px' }}
                    >
                      {isVisibleBroadcastKey ? (
                        <EyeOpenIcon
                          className={styles.eyeOpenIcon}
                          onClick={handleChangeBroadcastKeyVisibility}
                        />
                      ) : (
                        <EyeClosedIcon
                          className={styles.eyeClosedIcon}
                          onClick={handleChangeBroadcastKeyVisibility}
                        />
                      )}
                      <CopyIcon
                        className={styles.copyIcon}
                        onClick={() => copyToClipboard('key')}
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.fieldContainer}>
                  <div className={styles.fieldTitle}>Broadcast URL</div>
                  <div className={styles.fieldBox}>
                    <div className={styles.fieldBoxInner}>
                      <LockIcon className={styles.lockIcon} />
                      <div className={styles.fieldValue}>{broadcastURL}</div>
                    </div>
                    <CopyIcon
                      className={styles.copyIcon}
                      onClick={() => copyToClipboard('url')}
                    />
                  </div>
                </div>

                <div className={styles.fieldContainer}>
                  <div className={styles.fieldTitle}>Backup server URL</div>
                  <div className={styles.fieldBox}>
                    <div className={styles.fieldBoxInner}>
                      <LockIcon className={styles.lockIcon} />
                      <div className={styles.fieldValue}>{backupServerURL}</div>
                    </div>
                    <CopyIcon
                      className={styles.copyIcon}
                      onClick={() => copyToClipboard('serverUrl')}
                    />
                  </div>
                </div>
              </>
            ) : null}

            <Button
              type="button"
              disabled={titleCharactersTrimQuantity === 0}
              style={{
                width: '100%',
                height: '50px',
                minHeight: '50px',
                background:
                  'linear-gradient(131.69deg, #1f45c3 6.46%, #0e219b 100%)',
              }}
              styleText={{ color: '#ffffff' }}
              btnStyle={
                titleCharactersTrimQuantity === 0 ? 'disabled' : 'classic'
              }
              text={isFirstStreamOptions ? 'Next' : 'Save'}
              onClick={
                isFirstStreamOptions
                  ? saveFirstLiveStreamSettings
                  : updateLiveStreamSettings
              }
              blueHover
            />
          </div>
        }
      />
    )
  }
)
